// src/app/practice/PracticeClient.tsx
'use client'

import { useState, useMemo, useTransition } from 'react'
import SectionHero from '@/components/SectionHero'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { useRefetchOnFocus } from '@/hooks/use-refetch-on-focus'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Zap } from 'lucide-react'
import { getVideosForTopic } from '@/lib/content/topic-videos'
import {
  masteryTier,
  MASTERY_DISPLAY,
  MASTERY_MIN_ATTEMPTS,
  type MasteryTier,
} from '@/lib/test-engine/sat-domains'
import { TIER_ACCENT } from '@/lib/scoring/mastery-display'
import MasteryLegend from '@/components/MasteryLegend'
import { MasteryMedal } from '@/components/MasteryMedal'
import type { TopicPerformance } from './page'

// Tier-colored fill for the progress bar (the shadcn Progress indicator is the
// only child div, so the arbitrary child variant recolors it per tier).
const TIER_BAR: Record<MasteryTier, string> = {
  untouched: '',
  in_progress: '[&>*]:bg-rose-500',
  bronze: '[&>*]:bg-orange-600',
  silver: '[&>*]:bg-slate-500',
  gold: '[&>*]:bg-yellow-400',
}
import {
  filterAndSortTopics,
  groupBySection,
  topicCardKey,
  type SectionFilter,
  type SortMode,
  practiceSessionHref,
  type PracticeDifficulty,
} from './practice-filter'

export default function PracticeClient({
  topics,
  paid,
  newLook = false,
}: {
  topics: TopicPerformance[]
  paid: boolean
  /** Four-zone look (dashboard_v2). Server-resolved so the flag read stays off the client. */
  newLook?: boolean
}) {
  const router = useRouter()
  const [sectionFilter, setSectionFilter] = useState<SectionFilter>('ALL')
  const [sortMode, setSortMode] = useState<SortMode>('weakest')

  // The hub is server-rendered (force-dynamic) and receives topic mastery as
  // props, so a client re-render never updates the numbers on its own. When the
  // student comes back from a practice session — tab return, or the browser
  // Back button (bfcache) — refresh the route so the cards reflect the questions
  // they just answered, instead of the pre-session snapshot (Visakh: "status
  // isn't updating when I return; I have to refresh the page").
  // Wrap the refresh in a transition so we can tell the difference between
  // "this topic has genuinely never been practiced" and "we haven't re-read
  // the server yet". Without that the hub confidently renders "Not practiced"
  // from the stale cached payload while the refetch is in flight — which is
  // what a student sees right after finishing 10 questions on that very topic.
  const [refreshing, startRefresh] = useTransition()
  useRefetchOnFocus(() => startRefresh(() => router.refresh()))

  const filtered = useMemo(
    () => filterAndSortTopics(topics, sectionFilter, sortMode),
    [topics, sectionFilter, sortMode],
  )
  // Split into Reading & Writing / Math groups so the grid renders two labeled
  // sections instead of one intermixed wall. When a single section is selected
  // the filter already narrows to it, so there's just one group (rendered
  // without a redundant header — see `showGroupHeaders`).
  // Difficulty is a SESSION setting chosen before the session starts. It is
  // deliberately not persisted, matching Section and Sort above it: these are
  // "what do I want right now" controls, not preferences.
  const [difficulty, setDifficulty] = useState<PracticeDifficulty>('ADAPTIVE')

  const groups = useMemo(() => groupBySection(filtered), [filtered])
  const showGroupHeaders = sectionFilter === 'ALL' && groups.length > 1

  // Packaging v2: topic practice is FREE for everyone (unlimited, untimed).
  // The cross-topic Adaptive engine stays Pro (its own route). Sessions run with
  // count=all — they refill until the topic is exhausted or the student ends it.
  function startPractice(topic: string) {
    router.push(practiceSessionHref(topic, difficulty))
  }

  return (
    <>
      <main className="min-h-dvh bg-background text-foreground">
        <div className="mx-auto max-w-6xl px-4 py-8">
          {newLook ? (
            <div className="mb-4">
              <SectionHero
                kind="practice"
                title="Practice"
                subtitle="Drill any topic at your own pace — as much as you like."
              />
            </div>
          ) : (
            <h1 className="mb-2 text-2xl font-bold">Practice</h1>
          )}
          <p className="mb-6 text-sm text-muted-foreground">
            Focus on your weak areas. Set the difficulty below before you start, or change it any
            time inside the session — practice as many questions as you want, quit anytime. Prefer
            to explore?{' '}
            <Link
              href="/practice/browse"
              className="underline underline-offset-4 hover:text-foreground"
            >
              Browse the full question bank
            </Link>
            .
          </p>

          {/* Free users: topic practice is theirs; the cross-topic Adaptive
              engine (auto-targets your weakest areas) is the Pro upsell. */}
          {!paid && (
            <div className="mb-6 rounded-xl border border-brand/40 bg-brand/10 px-5 py-4 text-sm">
              <span className="font-semibold text-brand-ink">Topic practice is free —</span> pick
              any topic below and practice as much as you want. Want the engine to auto-target your
              weakest areas instead?{' '}
              <Link
                href="/pricing"
                className="font-medium text-brand-ink underline-offset-2 hover:underline dark:text-brand-ink"
              >
                Adaptive practice is part of Pro →
              </Link>
            </div>
          )}

          {/* Daily Drill CTA */}
          <Link
            href="/drills"
            className="mb-6 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 transition hover:bg-amber-500/15"
          >
            <Zap className="h-6 w-6 text-amber-400" />
            <div>
              <p className="font-semibold text-amber-700 dark:text-amber-300">Daily Drill</p>
              <p className="text-xs text-muted-foreground">
                Your free 12-question mixed set for today
              </p>
            </div>
          </Link>

          {/* Filters */}
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium uppercase text-muted-foreground">Section:</span>
              {(['ALL', 'RW', 'MATH'] as const).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={sectionFilter === s ? 'default' : 'outline'}
                  className={
                    sectionFilter === s
                      ? 'bg-brand text-on-brand hover:bg-brand-hover'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  }
                  onClick={() => setSectionFilter(s)}
                >
                  {s === 'ALL' ? 'All' : s}
                </Button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium uppercase text-muted-foreground">Sort:</span>
              {(
                [
                  ['weakest', 'Weakest first'],
                  ['strongest', 'Strongest first'],
                  ['az', 'A-Z'],
                ] as const
              ).map(([key, label]) => (
                <Button
                  key={key}
                  size="sm"
                  variant={sortMode === key ? 'default' : 'outline'}
                  className={
                    sortMode === key
                      ? 'bg-brand text-on-brand hover:bg-brand-hover'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  }
                  onClick={() => setSortMode(key)}
                >
                  {label}
                </Button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-medium uppercase text-muted-foreground">
                Difficulty:
              </span>
              {(
                [
                  ['ADAPTIVE', 'Adaptive'],
                  ['E', 'Easy'],
                  ['M', 'Medium'],
                  ['H', 'Hard'],
                ] as const
              ).map(([key, label]) => (
                <Button
                  key={key}
                  size="sm"
                  variant={difficulty === key ? 'default' : 'outline'}
                  className={
                    difficulty === key
                      ? 'bg-brand text-on-brand hover:bg-brand-hover'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  }
                  onClick={() => setDifficulty(key)}
                  aria-pressed={difficulty === key}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {/* Said once, here, because the control above only takes effect when a
              session starts — and a topic with nothing at the chosen level
              falls back to its mixed pool rather than refusing to start. */}
          <p className="-mt-4 mb-6 text-xs text-muted-foreground">
            {difficulty === 'ADAPTIVE'
              ? 'Adaptive moves the level with your answers. Pick Easy, Medium or Hard to stay at one.'
              : 'Every question will be at this level. Topics without enough of them fall back to a mix.'}
          </p>

          {/* Tier legend — identical vocabulary to the dashboard Mastery Map. */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase text-muted-foreground">Mastery:</span>
            <MasteryLegend />
          </div>

          {/* Topic grid — split into Reading & Writing / Math sections so the
              two subjects don't intermix into one wall of cards. Headers show
              only in the combined "All" view; a single-section filter renders
              one group without a redundant header. */}
          {filtered.length === 0 ? (
            <p className="text-muted-foreground">No topics available for the selected filter.</p>
          ) : (
            <div className="space-y-8">
              {groups.map((g) => (
                <section key={g.key}>
                  {showGroupHeaders && (
                    <div className="mb-3 flex items-center gap-2">
                      <span
                        aria-hidden
                        className={`h-2.5 w-2.5 rounded-full ${
                          g.key === 'MATH' ? 'bg-brand' : 'bg-brand'
                        }`}
                      />
                      <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground">
                        {g.label}
                      </h2>
                      <span className="text-xs text-muted-foreground">
                        {g.items.length} {g.items.length === 1 ? 'topic' : 'topics'}
                      </span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
                    {g.items.map((t) => {
                      const pct = t.total > 0 ? Math.round((t.correct / t.total) * 100) : null
                      // Same tier math as the dashboard Mastery Map: EXACT accuracy
                      // (not rounded) gated by attempts.
                      const tier = masteryTier(
                        t.total > 0 ? (t.correct / t.total) * 100 : 0,
                        t.total,
                      )
                      const tierInfo = MASTERY_DISPLAY[tier]
                      const hasMedal = tier === 'bronze' || tier === 'silver' || tier === 'gold'
                      return (
                        <Card
                          key={topicCardKey(t)}
                          // Lets a test scope assertions to ONE topic's card. The
                          // hub renders dozens, most legitimately "Not practiced",
                          // so an unscoped check for that text always matches some
                          // other card and proves nothing.
                          data-topic={t.topic}
                          className={`bg-card/60 ${TIER_ACCENT[tier]}`}
                          title={
                            hasMedal
                              ? `${t.topic} — ${tierInfo.label} (${pct}% over ${t.total} attempts)`
                              : t.total > 0
                                ? `${t.topic} — ${t.total}/${MASTERY_MIN_ATTEMPTS}+ attempts before a mastery tier applies`
                                : undefined
                          }
                        >
                          <CardHeader className="p-3 pb-1.5">
                            <div className="flex items-start justify-between gap-2">
                              <CardTitle className="text-sm leading-tight text-foreground">
                                {t.topic}
                              </CardTitle>
                              <span className="flex shrink-0 items-center gap-1.5">
                                {hasMedal && <MasteryMedal tier={tier} />}
                                <Badge
                                  variant="outline"
                                  className={
                                    t.section === 'MATH'
                                      ? 'border-brand/50 text-brand-ink'
                                      : 'border-brand/50 text-brand-ink'
                                  }
                                >
                                  {t.section}
                                </Badge>
                              </span>
                            </div>
                            {t.domain && (
                              <p className="text-xs text-muted-foreground">{t.domain}</p>
                            )}
                          </CardHeader>
                          <CardContent className="space-y-2 p-3 pt-0">
                            {pct !== null ? (
                              <div>
                                <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                                  <span>
                                    {t.rawCorrect ?? t.correct}/{t.total} correct
                                    {/* Mastery credits a correct guess at half
                                        (topic-mastery.ts), so the bar can sit
                                        below the plain count — say why. */}
                                    {(t.rawCorrect ?? t.correct) !== t.correct && (
                                      <span title="Answers you marked as a guess count half toward mastery.">
                                        {' '}
                                        · guesses count half
                                      </span>
                                    )}
                                  </span>
                                  <span>{pct}%</span>
                                </div>
                                <Progress value={pct} className={`h-1.5 ${TIER_BAR[tier]}`} />
                              </div>
                            ) : refreshing ? (
                              /* Indeterminate, not a claim — see the useTransition
                                 note above. */
                              <p className="text-xs italic text-muted-foreground">Updating…</p>
                            ) : (
                              <p className="text-xs italic text-muted-foreground">Not practiced</p>
                            )}
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                className="h-7 flex-1 bg-brand text-xs text-on-brand"
                                onClick={() => startPractice(t.topic)}
                                aria-label={`Practice ${t.topic}`}
                              >
                                Practice
                              </Button>
                              {getVideosForTopic(t.topic) && (
                                <Link
                                  href={`/learn?search=${encodeURIComponent(t.topic)}`}
                                  className="inline-flex h-7 items-center rounded-md border border-brand/50 px-2.5 text-xs font-medium text-brand-ink transition hover:bg-brand/15 hover:text-brand-ink dark:hover:bg-brand/40 dark:hover:text-brand-ink"
                                >
                                  Learn
                                </Link>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      )
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  )
}
